<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import {
    Button,
    ButtonVariant,
    Dropdown,
    DropdownItem,
  } from '@tundr/ds-components';
  import type { ActiveFilter } from '../../../store/';
  import { filtersSelectorWrapperClass } from './FilterSelector.css';
  import { useFilters } from '../../../composables';
  import { computed } from 'vue';
  import { FilterTypes, getFilterTypeIconByType } from '../../../utils';

  const { t } = useLocale('table');

  const props = defineProps<{
    filterNamespace: string;
    filtersConfig: ActiveFilter[];
    disabled?: boolean;
  }>();

  const currentFilterStore = useFilters(props.filterNamespace);

  const availableFiltersList = computed(() => {
    return props.filtersConfig.filter((filter) => {
      return (
        filter.filterType !== FilterTypes.TEXT_SEARCH &&
        !currentFilterStore.filterIsActive(filter.filterFieldName)
      );
    });
  });

  const isDisabled = computed(
    () => !!props.disabled || availableFiltersList.value.length === 0,
  );

  let pendingFilter: ActiveFilter | null = null;

  const onSelectFilter = (filter: ActiveFilter) => {
    pendingFilter = filter;
  };

  const onOpenChange = (open: boolean) => {
    const filter = pendingFilter;
    if (open || !filter) return;
    pendingFilter = null;
    const newFilter = { ...filter, modelValue: undefined };
    requestAnimationFrame(() => currentFilterStore.addFilter(newFilter));
  };
</script>

<template>
  <div :class="filtersSelectorWrapperClass">
    <Dropdown
      trigger-as-child
      :disabled="isDisabled"
      @update:open="onOpenChange"
    >
      <template #trigger>
        <Button
          :variant="ButtonVariant.Secondary"
          :icon-name="TUNDR_ICONS.SETTINGS"
          :disabled="isDisabled"
          test-id="table-filters-trigger"
        >
          {{ t('filters.filters_button_label') }}
        </Button>
      </template>
      <template
        v-for="filter in availableFiltersList"
        :key="filter.filterFieldName"
      >
        <DropdownItem
          :icon="filter.iconName || getFilterTypeIconByType(filter.filterType)"
          :label="filter.filterLabel"
          @click="onSelectFilter(filter)"
        />
      </template>
    </Dropdown>
  </div>
</template>
